// src/components/CustomerForm.js
import React, { useState, useEffect, useRef } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import styled from "styled-components";
import InputMask from "react-input-mask";
import {
  FormContainer,
  FormInnerContainer,
  SubmitButton,
  CancelButton,
  Input,
  Label,
  Select,
  Row,
  Column
} from "../styles/StyledComponents";

// Estilos personalizados para os botões de adicionar e remover
const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ add }) => (add ? "#28a745" : "#dc3545")};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 8px;
  font-size: 14px;
  cursor: pointer;
  margin-left: ${({ add }) => (add ? "8px" : "0")};
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ add }) => (add ? "#218838" : "#c82333")};
  }
`;

const ButtonGroup = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: flex-start;
  gap: 8px;
`;

const CustomerForm = ({ visible, customer, onChange, onSubmit, onCancel, isEditing }) => {
  const [equipmentList, setEquipmentList] = useState([]);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    setEquipmentList(
      customer.equipment && customer.equipment.length > 0
        ? customer.equipment
        : [{ equipmentNumber: "", apelido: "", type: "Social", manufacturer: "thyssenkrupp" }]
    );
  }, [customer.equipment]);

  if (!visible) return null;

  const handleAddEquipment = () => {
    setEquipmentList([
      ...equipmentList,
      { equipmentNumber: "", apelido: "", type: "Social", manufacturer: "thyssenkrupp" }
    ]);
  };

  const handleEquipmentChange = (index, field, value) => {
    const updatedEquipments = [...equipmentList];
    updatedEquipments[index][field] = value;
    setEquipmentList(updatedEquipments);
    onChange({ target: { name: "equipment", value: updatedEquipments } });
  };

  const handleRemoveEquipment = index => {
    if (equipmentList.length > 1) {
      const updatedEquipments = equipmentList.filter((_, i) => i !== index);
      setEquipmentList(updatedEquipments);
      onChange({ target: { name: "equipment", value: updatedEquipments } });
    }
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.address_components) {
      const components = {
        address: "",
        number: "",
        neighborhood: "",
        postalCode: "",
        city: "",
        state: "",
        latitude: place.geometry?.location?.lat() || "",
        longitude: place.geometry?.location?.lng() || ""
      };

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) components.number = component.long_name;
        if (types.includes("route")) components.address = component.long_name;
        if (types.includes("sublocality") || types.includes("neighborhood")) components.neighborhood = component.long_name;
        if (types.includes("postal_code")) components.postalCode = component.long_name;
        if (types.includes("locality")) components.city = component.long_name;
        if (types.includes("administrative_area_level_2")) components.city = component.long_name;
        if (types.includes("administrative_area_level_1")) components.state = component.short_name;
      });

      onChange({ target: { name: "address", value: components.address } });
      onChange({ target: { name: "number", value: components.number } });
      onChange({ target: { name: "neighborhood", value: components.neighborhood } });
      onChange({ target: { name: "postalCode", value: components.postalCode } });
      onChange({ target: { name: "city", value: components.city } });
      onChange({ target: { name: "state", value: components.state } });
      onChange({ target: { name: "latitude", value: components.latitude } });
      onChange({ target: { name: "longitude", value: components.longitude } });
    }
  };

  return (
    <FormContainer visible={visible}>
      <FormInnerContainer>
        <form onSubmit={onSubmit}>
          <Row columns={12}>
            <Column cols={12}>
              <h2>{isEditing ? "Editar Cliente" : "Novo Cliente"}</h2>
              <p>Complete os campos abaixo para criar ou editar um cliente.</p>
            </Column>
          </Row>

          <h4 style={{ marginLeft: "16px" }}>Dados do local de instalação</h4>
          <Row columns={12}>
            <Column cols={3}>
              <Label>Nr. Contrato</Label>
              <Input type="text" name="contractNumber" value={customer.contractNumber} onChange={onChange} required />
            </Column>
            <Column cols={3}>
              <Label>Status Contrato</Label>
              <Select name="status" value={customer.status} onChange={onChange} required>
                <option value="Ativo">Ativo</option>
                <option value="Suspenso">Suspenso</option>
                <option value="Cancelado">Cancelado</option>
                <option value="Lead - Sem contrato">Lead - Sem contrato</option>
              </Select>
            </Column>

            <Column cols={3}>
              <Label>Tipo Contrato</Label>
              <Select
  name="tipoContrato"
  value={customer.tipoContrato}
  onChange={(e) => {
    console.log("TROCA tipoContrato:", e.target.value);
    onChange({ target: { name: "tipoContrato", value: e.target.value } });
  }}
  required
>
  <option value="S - Service">S - Service</option>
  <option value="SP - Sercice Plus">SP - Sercice Plus</option>
  <option value="P - Premium">P - Premium</option>
  <option value="PP - Premium Plus">PP - Premium Plus</option>
</Select>
            </Column>

          </Row>

          <Row columns={12}>
            <Column cols={9}>
              <Label>Nome</Label>
              <Input type="text" name="name" value={customer.name} onChange={onChange} required />
            </Column>
            <Column cols={3}>
              <Label>CNPJ</Label>
              <InputMask mask="99.999.999/9999-99" value={customer.cnpj || ""} onChange={onChange}>
                {(inputProps) => <Input {...inputProps} type="text" name="cnpj" required />}
              </InputMask>
            </Column>
          </Row>

          <Row columns={12}>
            <Column cols={10}>
              <Label>Endereço</Label>
              <Autocomplete onLoad={(ref) => (autocompleteRef.current = ref)} onPlaceChanged={handlePlaceChanged}>
                <Input type="text" name="address" value={customer.address} onChange={onChange} required />
              </Autocomplete>
            </Column>
            <Column cols={2}>
              <Label>Número</Label>
              <Input type="text" name="number" value={customer.number} onChange={onChange} required />
            </Column>
          </Row>  

          <Row columns={12}>
            <Column cols={2}>
              <Label>CEP</Label>
              <Input type="text" name="postalCode" value={customer.postalCode} onChange={onChange} required />
            </Column>
            <Column cols={4}>
              <Label>Bairro</Label>
              <Input type="text" name="neighborhood" value={customer.neighborhood} onChange={onChange} required />
            </Column>
            <Column cols={4}>
              <Label>Cidade</Label>
              <Input type="text" name="city" value={customer.city} onChange={onChange} required />
            </Column>
            <Column cols={1}>
              <Label>Estado</Label>
              <Input type="text" name="state" value={customer.state} onChange={onChange} required />
            </Column>
          </Row>

          <Row columns={12}>
            <Column cols={3}>
              <Label>Latitude</Label>
              <Input type="number" name="latitude" value={customer.latitude} onChange={onChange} disabled/>
            </Column>
            <Column cols={3}>
              <Label>Longitude</Label>
              <Input type="number" name="longitude" value={customer.longitude} onChange={onChange} disabled/>
            </Column>
          </Row>

          <h4 style={{ marginLeft: "16px" }}>Dados para Contato</h4>
          <Row columns={12}>
            <Column cols={4}>
              <Label>Nome do Contato</Label>
              <Input type="text" name="contactName" value={customer.contactName} onChange={onChange}  />
            </Column>
            <Column cols={4}>
              <Label>Telefone do Contato</Label>
              <Input type="text" name="contactPhone" value={customer.contactPhone} onChange={onChange}  />
            </Column>
            <Column cols={4}>
              <Label>Email do Contato</Label>
              <Input type="email" name="contactEmail" value={customer.contactEmail} onChange={onChange}  />
            </Column>
          </Row>

          {/* Lista dinâmica de equipamentos */}
          <Row columns={12}>
            <h4 style={{ marginLeft: "16px" }}>Equipamentos</h4>
            {equipmentList.map((equipment, index) => (
              <Row columns={12} key={index}>
                <Column cols={2}>
                  <Label>Nro Equip.</Label>
                  <Input
                    type="text"
                    value={equipment.equipmentNumber}
                    onChange={(e) => handleEquipmentChange(index, "equipmentNumber", e.target.value)}
                    required
                  />
                </Column>
                <Column cols={2}>
                  <Label>Apelido</Label>
                  <Input
                    type="text"
                    value={equipment.apelido || ""}
                    onChange={(e) => handleEquipmentChange(index, "apelido", e.target.value)}
                  />
                </Column>
                <Column cols={2}>
                  <Label>Tipo</Label>
                  <Select
                    value={equipment.type}
                    onChange={(e) => handleEquipmentChange(index, "type", e.target.value)}
                    required
                  >
                    <option value="Social">Social</option>
                    <option value="Serviço">Serviço</option>
                    <option value="Carga">Carga</option>
                    <option value="Garagem">Garagem</option>
                    <option value="Maca">Maca</option>
                    <option value="Monta-pratos">Monta-pratos</option>
                  </Select>
                </Column>
                <Column cols={2}>
                  <Label>Fabricante</Label>
                  <Select
                    value={equipment.manufacturer}
                    onChange={(e) => handleEquipmentChange(index, "manufacturer", e.target.value)}
                    required
                  >
                    <option value="THYSSENKRUPP">THYSSENKRUPP</option>
                    <option value="ADTECH">ADTECH</option>
                    <option value="ATLAS SCHINDLER">ATLAS SCHINDLER</option>
                    <option value="DAIKEN">DAIKEN</option>
                    <option value="INFOLEV">INFOLEV</option>
                    <option value="KONE">KONE</option>
                    <option value="LGTECH">LGTECH</option>
                    <option value="MITSUBISHI">MITSUBISHI</option>
                    <option value="OTIS">OTIS</option>
                    <option value="SCHIMERSAL">SCHIMERSAL</option>
                    <option value="WT">WT</option>
                    <option value="OUTROS">OUTROS</option>
                  </Select>
                </Column>
                <Column cols={4}>
                  <ButtonGroup>
                    <IconButton type="button" onClick={handleAddEquipment} add={true}>
                      <AiOutlinePlus />
                    </IconButton>
                    {index > 0 && (
                      <IconButton type="button" onClick={() => handleRemoveEquipment(index)} add={false}>
                        <AiOutlineMinus />
                      </IconButton>
                    )}
                  </ButtonGroup>
                </Column>
              </Row>
            ))}
          </Row>

          <Row columns={12} mt={32} mb={150}>
            <Column cols={3}>
              <SubmitButton type="submit">Salvar</SubmitButton>
            </Column>
            <Column cols={3}>
              <CancelButton type="button" onClick={onCancel}>
                Cancelar
              </CancelButton>
            </Column>
          </Row>
        </form>
      </FormInnerContainer>
    </FormContainer>
  );
};

export default CustomerForm;
